<template>
  <v-card outlined class="mb-4">
    <v-card-text>
      <v-alert type="info" outlined class="mb-4">
        <v-icon left>mdi-information</v-icon>
        Pregledajte sve podatke prije potvrde proizvodnje
      </v-alert>

      <!-- Source Items Section -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-1 primary white--text">
          <v-icon left color="white">mdi-arrow-down-bold</v-icon>
          Polazni materijal
        </v-card-title>
        <v-card-subtitle class="white pt-2 pb-2">
          <v-chip small color="primary" outlined>
            <v-icon left small>mdi-warehouse</v-icon>
            {{ sourceWarehouse ? sourceWarehouse.name : 'N/A' }}
          </v-chip>
        </v-card-subtitle>
        <v-card-text>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Artikl</th>
                  <th class="text-right">Količina</th>
                  <th class="text-right">Cijena/kg</th>
                  <th class="text-right">Ukupno</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in validSourceItems" :key="index">
                  <td>
                    <div>{{ item.item.name }}</div>
                    <div class="caption grey--text">{{ item.item.code }}</div>
                  </td>
                  <td class="text-right">{{ Number(item.quantity).toFixed(2) }} kg</td>
                  <td class="text-right">{{ Number(item.item.pricePerKg).toFixed(2) }} €</td>
                  <td class="text-right">
                    <strong>{{ (Number(item.quantity) * Number(item.item.pricePerKg)).toFixed(2) }} €</strong>
                  </td>
                </tr>
                <tr class="grey lighten-4">
                  <td colspan="3" class="text-right"><strong>Ukupna vrijednost sirovine:</strong></td>
                  <td class="text-right">
                    <strong class="primary--text">{{ totalSourceValue.toFixed(2) }} €</strong>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Production Arrow -->
      <div class="text-center mb-4">
        <v-icon color="success" size="48">mdi-arrow-down-bold-circle</v-icon>
        <div class="text-h6 success--text">PROIZVODNJA</div>
      </div>

      <!-- Output Products Section -->
      <v-card outlined class="mb-4" color="success lighten-5">
        <v-card-title class="subtitle-1 success white--text">
          <v-icon left color="white">mdi-package-variant</v-icon>
          Gotovi proizvodi ({{ outputProducts.length }})
        </v-card-title>
        <v-card-text>
          <v-card
            v-for="(product, productIndex) in outputProducts"
            :key="productIndex"
            outlined
            class="mb-3"
          >
            <v-card-subtitle class="pb-2">
              <v-chip small color="success" outlined>
                <v-icon left small>mdi-warehouse</v-icon>
                {{ product.warehouse ? product.warehouse.name : 'N/A' }}
              </v-chip>
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined>
                    <v-card-text>
                      <div class="caption grey--text">Proizvod</div>
                      <div class="text-h6">{{ product.item ? product.item.name : 'N/A' }}</div>
                      <div class="caption">{{ product.item ? product.item.code : '' }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card outlined color="success lighten-4">
                    <v-card-text>
                      <div class="caption grey--text">Količina</div>
                      <div class="text-h6 success--text">{{ Number(product.quantity).toFixed(2) }} {{ product.item ? product.item.unit : 'kg' }}</div>
                      <div class="caption">{{ product.item ? (Number(product.quantity) * Number(product.item.recipeYield)).toFixed(0) : 0 }} porcija</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-divider class="my-3"></v-divider>

              <div class="subtitle-2 mb-2">Receptura:</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Sastojak</th>
                      <th class="text-right">Po jedinici</th>
                      <th class="text-right">Ukupno potrebno</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(ingredient, index) in product.recipe" :key="index">
                      <td>{{ ingredient.sourceItem ? ingredient.sourceItem.name : 'N/A' }}</td>
                      <td class="text-right">{{ Number(ingredient.quantityPerUnit).toFixed(2) }} kg</td>
                      <td class="text-right">
                        <strong>{{ (Number(ingredient.quantityPerUnit) * Number(product.quantity)).toFixed(2) }} kg</strong>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>

      <!-- Material Balance Section -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-1">
          <v-icon left>mdi-scale-balance</v-icon>
          Balans materijala
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="balanceStatus.hasOverused"
            type="error"
            dense
            outlined
            class="mb-3"
          >
            <v-icon left small>mdi-alert-circle</v-icon>
            Koristite više materijala nego što je dostupno!
          </v-alert>

          <v-alert
            v-if="balanceStatus.hasUnused && !balanceStatus.hasOverused"
            type="warning"
            dense
            outlined
            class="mb-3"
          >
            <v-icon left small>mdi-alert</v-icon>
            Imate neiskorištene materijale.
          </v-alert>

          <v-alert
            v-if="!balanceStatus.hasOverused && !balanceStatus.hasUnused"
            type="success"
            dense
            outlined
            class="mb-3"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Svi materijali su balansirani!
          </v-alert>

          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Materijal</th>
                  <th class="text-right">Originalno</th>
                  <th class="text-right">Kalo</th>
                  <th class="text-right">Za korištenje</th>
                  <th class="text-right">Iskorišteno</th>
                  <th class="text-right">Preostalo</th>
                  <th class="text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="material in balanceStatus.materialBalance" :key="material.id">
                  <td>{{ material.name }}</td>
                  <td class="text-right">
                    {{ material.originalAvailable.toFixed(2) }} {{ material.unit }}
                  </td>
                  <td class="text-right">
                    <span v-if="material.wastagePercentage > 0" class="orange--text">
                      {{ material.wastagePercentage }}%
                      <div class="caption">-{{ material.wastageAmount.toFixed(2) }} {{ material.unit }}</div>
                    </span>
                    <span v-else class="grey--text">-</span>
                  </td>
                  <td class="text-right">
                    <strong>{{ material.available.toFixed(2) }} {{ material.unit }}</strong>
                  </td>
                  <td class="text-right">{{ material.used.toFixed(2) }} {{ material.unit }}</td>
                  <td class="text-right">
                    <span :class="material.remaining < 0 ? 'error--text font-weight-bold' : ''">
                      {{ material.remaining.toFixed(2) }} {{ material.unit }}
                    </span>
                  </td>
                  <td class="text-right">
                    <v-chip
                      small
                      :color="getBalanceColor(material)"
                      text-color="white"
                    >
                      {{ getBalanceStatus(material) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <!-- Cost Analysis -->
      <v-card outlined color="blue lighten-5">
        <v-card-title class="subtitle-1">
          <v-icon left>mdi-calculator</v-icon>
          Analiza troškova
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col cols="12">
              <v-card outlined>
                <v-card-text class="text-center">
                  <div class="caption grey--text">Ukupna vrijednost sirovine</div>
                  <div class="text-h6">{{ totalSourceValue.toFixed(2) }} €</div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <div class="subtitle-2 mb-2">Cijena po proizvodu:</div>
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Proizvod</th>
                  <th class="text-right">Količina</th>
                  <th class="text-right">Troškovi sirovine</th>
                  <th class="text-right">Po kg/kom</th>
                  <th class="text-right">Po porciji</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in outputProducts" :key="index">
                  <td>{{ product.item ? product.item.name : 'N/A' }}</td>
                  <td class="text-right">{{ Number(product.quantity).toFixed(2) }} {{ product.item ? product.item.unit : 'kg' }}</td>
                  <td class="text-right">
                    <strong>{{ getProductCost(product).toFixed(2) }} €</strong>
                  </td>
                  <td class="text-right">
                    {{ getProductCostPerUnit(product).toFixed(2) }} €
                  </td>
                  <td class="text-right">
                    {{ getProductCostPerPortion(product).toFixed(2) }} €
                  </td>
                </tr>
                <tr class="grey lighten-4">
                  <td colspan="2" class="text-right"><strong>UKUPNO:</strong></td>
                  <td class="text-right">
                    <strong class="primary--text">{{ totalProductsCost.toFixed(2) }} €</strong>
                  </td>
                  <td colspan="2"></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ProductionSummary',
  props: {
    sourceItems: {
      type: Array,
      default: () => []
    },
    sourceWarehouse: {
      type: Object,
      default: () => null
    },
    outputProducts: {
      type: Array,
      default: () => []
    },
    balanceStatus: {
      type: Object,
      default: () => ({
        isBalanced: false,
        hasUnused: false,
        hasOverused: false,
        materialBalance: []
      })
    }
  },
  computed: {
    validSourceItems () {
      return this.sourceItems.filter(item => item && item.item && item.item.name && item.item.pricePerKg !== null && item.item.pricePerKg !== undefined)
    },
    totalSourceValue () {
      return this.validSourceItems.reduce((sum, item) => {
        const qty = Number(item.quantity) || 0
        const price = Number(item.item.pricePerKg) || 0
        return sum + (qty * price)
      }, 0)
    },
    totalProductsCost () {
      return this.outputProducts.reduce((sum, product) => {
        return sum + this.getProductCost(product)
      }, 0)
    }
  },
  methods: {
    getProductCost (product) {
      if (!product.recipe || product.recipe.length === 0) return 0

      return product.recipe.reduce((sum, ingredient) => {
        if (!ingredient.sourceItem || !ingredient.quantityPerUnit) return sum

        // Find the source item to get the price
        const sourceItem = this.sourceItems.find(si => si.item && si.item.id === ingredient.sourceItem.id)
        if (!sourceItem || !sourceItem.item || !sourceItem.item.pricePerKg) return sum

        const qtyPerUnit = Number(ingredient.quantityPerUnit) || 0
        const productQty = Number(product.quantity) || 0
        const pricePerKg = Number(sourceItem.item.pricePerKg) || 0

        const totalUsed = qtyPerUnit * productQty
        const cost = totalUsed * pricePerKg

        return sum + cost
      }, 0)
    },
    getProductCostPerUnit (product) {
      const qty = Number(product.quantity) || 0
      if (qty === 0) return 0
      return this.getProductCost(product) / qty
    },
    getProductCostPerPortion (product) {
      if (!product.item) return 0
      const qty = Number(product.quantity) || 0
      const recipeYield = Number(product.item.recipeYield) || 1
      const totalPortions = qty * recipeYield
      if (totalPortions === 0) return 0
      return this.getProductCost(product) / totalPortions
    },
    getBalanceColor (material) {
      if (material.remaining < -0.01) return 'error'
      if (material.remaining > 0.01) return 'warning'
      return 'success'
    },
    getBalanceStatus (material) {
      if (material.remaining < -0.01) return 'Prekoračeno'
      if (material.remaining > 0.01) return 'Neiskorišteno'
      return 'Balansirano'
    }
  }
}
</script>

<style scoped>
.v-card {
  transition: all 0.3s;
}
</style>
